import { useState, useMemo } from "react";
import { NumberField, ToolShell } from "./parts";
import { calcEmi, inr, inrShort } from "@/lib/format";

export function RentVsBuy() {
  const [price, setPrice] = useState(8000000);
  const [downPct, setDownPct] = useState(20);
  const [rate, setRate] = useState(8.5);
  const [years, setYears] = useState(20);
  const [monthlyRent, setMonthlyRent] = useState(25000);
  const [rentInflation, setRentInflation] = useState(7);
  const [appreciation, setAppreciation] = useState(7);
  const [altReturn, setAltReturn] = useState(10);

  const { verdict, nwBuy, nwRent, totalEmi, propValueEnd, totalRent, gap } = useMemo(() => {
    const down = (price * downPct) / 100;
    const loan = price - down;
    const emi = calcEmi(loan, rate, years);
    const months = years * 12;
    const totalEmi = emi * months;

    // BUY: loan repaid over the horizon, so end wealth = the home's value.
    const propValueEnd = price * Math.pow(1 + appreciation / 100, years);
    const nwBuy = propValueEnd;

    // RENT: invest the down payment up front, then each month invest the
    // difference between what the buyer's EMI would be and the rent paid.
    const rm = altReturn / 12 / 100;
    let corpus = down;
    let totalRent = 0;
    for (let m = 0; m < months; m++) {
      const yr = Math.floor(m / 12);
      const rentThisMonth = monthlyRent * Math.pow(1 + rentInflation / 100, yr);
      totalRent += rentThisMonth;
      const monthlySaving = emi - rentThisMonth; // may be negative in later years
      corpus = corpus * (1 + rm) + monthlySaving;
    }
    const nwRent = corpus;

    const verdict = nwBuy >= nwRent ? "Buying" : "Renting";
    return { verdict, nwBuy, nwRent, totalEmi, propValueEnd, totalRent, gap: Math.abs(nwBuy - nwRent) };
  }, [price, downPct, rate, years, monthlyRent, rentInflation, appreciation, altReturn]);

  const buyWins = nwBuy >= nwRent;

  return (
    <ToolShell title="Rent vs Buy" desc="Over your time horizon, is it smarter to buy, or to keep renting and invest the difference? We project the wealth you'd end up with in each path — the higher one wins.">
      <div className="grid gap-10 lg:grid-cols-2">
        <div className="space-y-7">
          <NumberField label="Property Price" value={price} onChange={setPrice} min={1000000} max={200000000} step={100000} prefix="₹" testid="rvb-price" />
          <NumberField label="Down Payment" value={downPct} onChange={setDownPct} min={10} max={100} step={1} suffix="%" testid="rvb-down" />
          <NumberField label="Loan Rate" value={rate} onChange={setRate} min={5} max={18} step={0.05} suffix="%" testid="rvb-rate" />
          <NumberField label="Horizon / Tenure" value={years} onChange={setYears} min={1} max={30} step={1} suffix="yrs" testid="rvb-years" />
          <NumberField label="Current Monthly Rent" value={monthlyRent} onChange={setMonthlyRent} min={2000} max={1000000} step={1000} prefix="₹" testid="rvb-rent" />
          <NumberField label="Rent Inflation" value={rentInflation} onChange={setRentInflation} min={0} max={15} step={0.5} suffix="%" testid="rvb-rentinf" />
          <NumberField label="Property Appreciation" value={appreciation} onChange={setAppreciation} min={0} max={15} step={0.5} suffix="%" testid="rvb-appr" />
          <NumberField label="Return if you invest instead" value={altReturn} onChange={setAltReturn} min={0} max={20} step={0.5} suffix="%" testid="rvb-alt" />
        </div>
        <div className="space-y-5">
          <div className="border-2 border-foreground bg-secondary p-6" data-testid="rvb-result">
            <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Smarter choice</p>
            <p className="mt-2 font-display text-4xl font-extrabold uppercase text-foreground" data-testid="rvb-verdict">{verdict}</p>
            <p className="mt-2 font-mono text-sm text-muted-foreground">ahead by {inrShort(gap)} after {years} years</p>
          </div>
          <div className={`border p-5 ${buyWins ? "border-foreground" : "border-border"} bg-background`}>
            <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Wealth if you BUY</p>
            <p className="mt-1 font-mono text-2xl font-bold text-foreground" data-testid="rvb-nw-buy">{inr(nwBuy)}</p>
            <p className="mt-1 font-mono text-xs text-muted-foreground">Home worth {inrShort(propValueEnd)} · EMIs paid {inrShort(totalEmi)}</p>
          </div>
          <div className={`border p-5 ${!buyWins ? "border-foreground" : "border-border"} bg-background`}>
            <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Wealth if you RENT & invest</p>
            <p className="mt-1 font-mono text-2xl font-bold text-foreground" data-testid="rvb-nw-rent">{inr(nwRent)}</p>
            <p className="mt-1 font-mono text-xs text-muted-foreground">Investment corpus · Rent paid {inrShort(totalRent)}</p>
          </div>
          <p className="font-mono text-[10px] text-muted-foreground">We assume the loan is fully repaid over the horizon, and a renter invests both the down payment and the monthly gap between EMI and rent at the investment return. Higher end wealth wins.</p>
        </div>
      </div>
    </ToolShell>
  );
}
